import { API_BASE_URL, getHeaders } from '../config/api';

const VAPID_KEY_CACHE_KEY = 'boviprice_vapid_public_key';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

async function getVapidPublicKey() {
  const cached = sessionStorage.getItem(VAPID_KEY_CACHE_KEY);
  if (cached) return cached;

  const response = await fetch(`${API_BASE_URL}/push/vapid-key`, {
    headers: { 'Accept': 'application/json' },
  });

  if (!response.ok) throw new Error('Failed to fetch VAPID key');

  const data = await response.json();
  sessionStorage.setItem(VAPID_KEY_CACHE_KEY, data.key);
  return data.key;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return null;
  }
}

export async function subscribeToPush(authToken) {
  if (!isPushSupported()) {
    return { success: false, reason: 'not_supported' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { success: false, reason: 'permission_denied' };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const vapidKey = await getVapidPublicKey();

    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }

    const subscriptionData = subscription.toJSON();

    const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
      method: 'POST',
      headers: getHeaders(authToken),
      body: JSON.stringify({
        endpoint: subscriptionData.endpoint,
        p256dh_key: subscriptionData.keys.p256dh,
        auth_token: subscriptionData.keys.auth,
      }),
    });

    if (!response.ok) throw new Error('Failed to save subscription on server');

    return { success: true };
  } catch (error) {
    console.error('Push subscription failed:', error);
    return { success: false, reason: 'subscription_error', error };
  }
}

export async function unsubscribeFromPush(authToken) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();

      await fetch(`${API_BASE_URL}/push/unsubscribe`, {
        method: 'DELETE',
        headers: getHeaders(authToken),
        body: JSON.stringify({ endpoint }),
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Push unsubscribe failed:', error);
    return { success: false, error };
  }
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}
